import React, { useEffect, useState, useCallback } from "react";
import api, { apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { StatusBadge } from "@/components/common/StatusBadge";
import { EmptyState } from "@/components/common/EmptyState";
import { toast } from "sonner";
import { Inbox, CheckCircle2, XCircle, Repeat, Search } from "lucide-react";

const TABS = [
  { key: "area_review_pending", label: "Needs Review" },
  { key: "email_verification_pending", label: "Awaiting Verification" },
  { key: "approved", label: "Approved" },
  { key: "declined", label: "Declined" },
  { key: "", label: "All" },
];

const MATCH_BADGE = { matched: "active", needs_review: "in_review", none: "inactive" };

export default function Registrations() {
  const [tab, setTab] = useState("area_review_pending");
  const [items, setItems] = useState([]);
  const [orgs, setOrgs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [decision, setDecision] = useState(null); // {type, reg}

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, o] = await Promise.all([
        api.get("/registrations", { params: tab ? { status: tab } : {} }),
        api.get("/organizations"),
      ]);
      setItems(r.data.items || []);
      setOrgs(o.data.organizations || []);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  }, [tab]);
  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-2xl font-bold text-slate-900">Prospect Approvals</h1>
          <p className="text-sm text-slate-500 mt-0.5">Review and approve candidate registrations for your districts.</p>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap gap-2 border-b border-slate-200">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)} data-testid={`reg-tab-${t.key || "all"}`}
            className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px transition-colors ${tab === t.key ? "border-[#006090] text-[#006090]" : "border-transparent text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <p className="text-sm text-slate-400 mt-8">Loading…</p>
      ) : items.length === 0 ? (
        <div className="mt-10"><EmptyState icon={Inbox} title="No registrations" description="Nothing in this queue right now." /></div>
      ) : (
        <div className="mt-6 overflow-x-auto tr-scroll rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm" data-testid="registrations-table">
            <thead>
              <tr className="text-left text-xs font-semibold text-slate-500 border-b border-slate-200 bg-slate-50">
                {["Name", "Contact", "Location", "Account Type", "Source", "Verified", "Suggested District", "Area", "Status", "Actions"].map((h) => (
                  <th key={h} className="px-3 py-2.5 whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {items.map((r) => (
                <tr key={r.id} className="border-b border-slate-100 align-top" data-testid={`reg-row-${r.id}`}>
                  <td className="px-3 py-3">
                    <p className="font-medium text-slate-800">{r.first_name} {r.last_name}</p>
                    <p className="text-xs text-slate-400 font-mono">{r.human_id}</p>
                  </td>
                  <td className="px-3 py-3 text-slate-600">
                    <p>{r.email}</p><p className="text-xs text-slate-400">{r.mobile}</p>
                  </td>
                  <td className="px-3 py-3 text-slate-600">{r.city}, {r.state} {r.zip_code}</td>
                  <td className="px-3 py-3 text-slate-600">{r.requested_account_type_label || (r.interest || "—").replace(/_/g, " ")}</td>
                  <td className="px-3 py-3 text-slate-600">
                    {r.source || "—"}
                    {r.referral_source_name && <p className="text-xs text-slate-400">Ref: {r.referral_source_name}</p>}
                  </td>
                  <td className="px-3 py-3">{r.email_verified ? <StatusBadge status="active" label="Yes" /> : <StatusBadge status="inactive" label="No" />}</td>
                  <td className="px-3 py-3 text-slate-600">{r.suggested_district_name || "—"}</td>
                  <td className="px-3 py-3">{r.area_match ? <StatusBadge status={MATCH_BADGE[r.area_match] || "inactive"} label={r.area_match.replace(/_/g, " ")} /> : "—"}</td>
                  <td className="px-3 py-3"><StatusBadge status={r.status === "approved" ? "active" : r.status === "declined" ? "inactive" : "in_review"} label={r.status.replace(/_/g, " ")} /></td>
                  <td className="px-3 py-3">
                    <div className="flex flex-wrap gap-1.5">
                      <Button size="sm" variant="outline" onClick={() => setDecision({ type: "view", reg: r })} data-testid={`reg-view-${r.id}`} className="h-8">View</Button>
                    {r.status !== "approved" && r.status !== "declined" && (
                      <>
                        <Button size="sm" onClick={() => setDecision({ type: "approve", reg: r })} data-testid={`reg-approve-${r.id}`} className="bg-[#0D9488] hover:bg-[#0b7d72] h-8">
                          <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDecision({ type: "change", reg: r })} data-testid={`reg-change-${r.id}`} className="h-8">
                          Change District
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDecision({ type: "review", reg: r })} data-testid={`reg-review-${r.id}`} className="h-8">
                          <Repeat className="w-3.5 h-3.5 mr-1" /> Review
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => setDecision({ type: "decline", reg: r })} data-testid={`reg-decline-${r.id}`} className="h-8 text-rose-600">
                          <XCircle className="w-3.5 h-3.5 mr-1" /> Decline
                        </Button>
                      </>
                    )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {decision && <DecisionDialog decision={decision} orgs={orgs} onClose={() => setDecision(null)} onDone={() => { setDecision(null); load(); }} />}
    </div>
  );
}

function DecisionDialog({ decision, orgs, onClose, onDone }) {
  const { type, reg } = decision;
  const [org, setOrg] = useState(reg.suggested_district_org_id || "");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const needsOrg = type === "approve" || type === "change";

  const submit = async () => {
    if (needsOrg && !org) return toast.error("Select a district.");
    setBusy(true);
    try {
      const url = { approve: "approve", decline: "decline", change: "change-district", review: "request-review" }[type];
      await api.post(`/registrations/${reg.id}/${url}`, { organization_id: needsOrg ? org : undefined, reason: reason || undefined });
      toast.success({ approve: "Registration approved", decline: "Registration declined", change: "District updated", review: "Sent to review" }[type]);
      onDone();
    } catch (e) { toast.error(apiError(e)); }
    finally { setBusy(false); }
  };

  const title = { approve: "Approve Registration", decline: "Decline Registration", change: "Change District", review: "Request Area Review", view: "Registration Details" }[type];
  const d = reg.details || {};
  const detailRows = [
    ["Requested Account Type", reg.requested_account_type_label],
    ["Agency Owner Path", d.agency_owner_interest], ["Position Interest", d.position_interest],
    ["License Status", d.license_status], ["Current Company", d.current_company],
    ["Current Role", d.current_role], ["Experience", d.experience],
    ["Open to Relocation", d.open_to_relocation], ["Preferred Location", d.preferred_location],
    ["Primary Markets", d.primary_markets], ["Referral Type", d.referral_type],
    ["Company / Organization", d.company_name], ["Expected Referrals", d.referral_candidates],
    ["LinkedIn", d.linkedin_url], ["Other Interest", d.other_interest_details],
    ["How they heard", reg.source], ["Person / Company Referral", reg.referral_source_name],
    ["Other Source Detail", reg.referral_source_other],
    ["Email", reg.email], ["Mobile", reg.mobile],
    ["Address", `${reg.address || ""}, ${reg.city || ""}, ${reg.state || ""} ${reg.zip_code || ""}`],
    ["Email Verified", reg.email_verified ? "Yes" : "No"],
    ["Suggested District", reg.suggested_district_name],
    ["Consent", reg.consent ? `Privacy ${reg.consent.privacy ? "✓" : "✗"} · Terms ${reg.consent.terms ? "✓" : "✗"} · Marketing ${reg.consent.recruiting_opt_in ? "✓" : "✗"}` : null],
    ["Registered", (reg.submitted_at || "").slice(0, 10)],
  ].filter(([, v]) => v && String(v).trim() && String(v).trim() !== ",  ");
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="bg-white max-h-[85vh] overflow-y-auto" data-testid="reg-decision-dialog">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{reg.first_name} {reg.last_name} · {reg.city}, {reg.state} {reg.zip_code}</DialogDescription>
        </DialogHeader>
        {type === "view" && (
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm" data-testid="reg-detail-view">
            {detailRows.map(([k, v]) => (
              <div key={k} className="border-b border-slate-100 py-1.5">
                <p className="text-[11px] font-semibold text-slate-400 uppercase">{k}</p>
                <p className="text-slate-700 capitalize">{String(v).replace(/_/g, " ")}</p>
              </div>
            ))}
            <p className="sm:col-span-2 mt-2 text-xs text-slate-500">This is the applicant's expressed interest only. Role, district, classification, and program are assigned by you at approval.</p>
          </div>
        )}
        {needsOrg && (
          <div>
            <label className="text-xs font-semibold text-slate-600">District</label>
            <Select value={org} onValueChange={setOrg}>
              <SelectTrigger className="mt-1.5" data-testid="reg-district-select"><SelectValue placeholder="Select a district" /></SelectTrigger>
              <SelectContent className="bg-white max-h-72">
                {orgs.map((o) => <SelectItem key={o.id} value={o.id}>{o.name}</SelectItem>)}
              </SelectContent>
            </Select>
            {type === "approve" && <p className="mt-2 text-xs text-slate-500">Approving activates the account, assigns the Candidate role and district, and creates/links the Person record. Candidate Type is left for the recruiter to decide later.</p>}
          </div>
        )}
        {(type === "approve" || type === "decline") && (
          <Textarea className="mt-2" placeholder="Optional note…" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="reg-decision-reason" />
        )}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>{type === "view" ? "Close" : "Cancel"}</Button>
          {type !== "view" && (
            <Button onClick={submit} disabled={busy} data-testid="reg-decision-confirm"
              className={type === "decline" ? "bg-rose-600 hover:bg-rose-700" : "bg-[#0D9488] hover:bg-[#0b7d72]"}>
              {title}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
